/**
 * The parent item, for rules that read it (lib/dynamicValues): its name and
 * every cell with its column's title and type, so a rule finds its column the
 * way columns are matched everywhere else.
 *
 *   { name, columns: [{ id, title, type, value, text }] }
 *
 * A subitem template's parent is the current item: fetchParentItem reads it
 * once per apply, useParentItem once per editor for the previews. A group
 * template's subitems have the template's own item row as their parent
 * (parentFromCells in lib/dynamicValues), so nothing is read for them.
 */
import { useEffect, useState } from 'react';
import { describeApiErrors } from './useBoardColumns';

export const PARENT_ITEM_QUERY = `query ($itemId: [ID!]) {
  items (ids: $itemId) { id name column_values { id type text value column { title } } }
}`;

export async function fetchParentItem(api, itemId) {
  const res = await api.api(PARENT_ITEM_QUERY, { variables: { itemId: [String(itemId)] } });
  const error = describeApiErrors(res);
  if (error) throw new Error(error);
  const item = res && res.data && res.data.items && res.data.items[0];
  if (!item) throw new Error('The item could not be read');
  return {
    name: item.name || '',
    columns: (item.column_values || []).map((cv) => ({
      id: cv.id,
      title: (cv.column && cv.column.title) || '',
      type: cv.type,
      value: cv.value,
      text: cv.text,
    })),
  };
}

/** The current item for the editor's previews; no item (a board view) → parent null. */
export default function useParentItem(api, itemId) {
  const [state, setState] = useState({ loading: Boolean(itemId), error: null, parent: null });

  useEffect(() => {
    if (!itemId) {
      setState({ loading: false, error: null, parent: null });
      return undefined;
    }
    let alive = true;
    setState({ loading: true, error: null, parent: null });
    fetchParentItem(api, itemId)
      .then((parent) => alive && setState({ loading: false, error: null, parent }))
      .catch((e) => {
        console.error('Could not load the parent item', e);
        if (alive) setState({ loading: false, error: e, parent: null });
      });
    return () => {
      alive = false;
    };
  }, [api, itemId]);

  return state;
}
